import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSocket } from '@/hooks/useSocket';

export function useConversations() {
  return useQuery({
    queryKey: ['conversations'],
    queryFn: () => api.getConversations(),
  });
}

export function useMessages(friendId: string | undefined) {
  const queryClient = useQueryClient();
  const { on, off, isConnected } = useSocket();

  const query = useQuery({
    queryKey: ['messages', friendId],
    queryFn: () => api.getMessages(friendId!),
    enabled: !!friendId,
  });

  useEffect(() => {
    if (!isConnected || !friendId) return;

    const handleMessageReceived = (data: {
      message: {
        id: string;
        senderId: string;
        senderName: string;
        content: string;
        createdAt: string;
      };
    }) => {
      // Always invalidate conversations so unread counts update across all chats
      queryClient.invalidateQueries({ queryKey: ['conversations'] });

      // Only invalidate the current chat's messages if the message is from this friend
      if (data.message.senderId === friendId) {
        queryClient.invalidateQueries({ queryKey: ['messages', friendId] });
      }
    };

    on('message:received', handleMessageReceived);

    return () => {
      off('message:received', handleMessageReceived);
    };
  }, [isConnected, friendId, on, off, queryClient]);

  return query;
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ friendId, content }: { friendId: string; content: string }) =>
      api.sendMessage(friendId, content),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages', variables.friendId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useMarkAsRead(friendId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!friendId) throw new Error('No friendId provided');
      return api.markMessagesAsRead(friendId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}
